// services/sync/CrossTabManager.js

import { cacheManager } from '@/services/app/CacheManager';
import { logoutService } from '@/services/auth/logout';

/**
 * CrossTabManager - Simplified cross-tab synchronization
 * 
 * Phase 4 implementation that makes cross-tab behavior predictable:
 * - Only syncs on critical events (login/logout)
 * - Prevents event loops and race conditions
 * - Cleans up stale events automatically
 * - Clear event handling with deduplication
 */
class CrossTabManager {
  constructor() {
    this.initialized = false;
    this.eventHandlers = new Map();
    this.processedEvents = new Map();
    this.eventCleanupInterval = null;
    
    // Configuration
    this.config = {
      eventTTL: 10000, // 10 seconds
      cleanupInterval: 30000, // 30 seconds
      deduplicationWindow: 5000 // 5 seconds
    };
  }

  /**
   * Initialize cross-tab sync
   */
  initialize() {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }

    // CrossTabManager init logging silenced

    // Clean up any stale events on startup
    this.cleanupStaleEvents();

    // Set up storage event listener
    window.addEventListener('storage', this.handleStorageEvent.bind(this));

    // Start periodic cleanup
    this.startPeriodicCleanup();

    // Register default handlers
    this.registerDefaultHandlers();

    this.initialized = true;
  }

  /**
   * Shutdown cross-tab sync
   */
  shutdown() {
    if (!this.initialized) {
      return;
    }

    // CrossTabManager shutdown logging silenced

    // Remove event listener
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));

    // Stop cleanup interval
    if (this.eventCleanupInterval) {
      clearInterval(this.eventCleanupInterval);
      this.eventCleanupInterval = null;
    }

    // Clear handlers
    this.eventHandlers.clear();
    this.processedEvents.clear();

    this.initialized = false;
  }

  /**
   * Register an event handler
   */
  registerHandler(eventType, handler) {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    
    this.eventHandlers.get(eventType).push(handler);
    // Handler registration logging silenced
  }

  /**
   * Emit an event to other tabs
   */
  emit(eventType, data = {}) {
    try {
      const event = {
        type: eventType,
        data,
        timestamp: Date.now(),
        tabId: this.getTabId()
      };

      const key = `crossTab_${eventType}`;
      localStorage.setItem(key, JSON.stringify(event));
      // Event emit logging silenced

      // Clean up the event after TTL
      setTimeout(() => {
        try {
          const currentEvent = localStorage.getItem(key);
          if (currentEvent) {
            const parsed = JSON.parse(currentEvent);
            // Only remove if it's our event (same timestamp)
            if (parsed.timestamp === event.timestamp) {
              localStorage.removeItem(key);
            }
          }
        } catch (e) {
          // Ignore
        }
      }, this.config.eventTTL);

    } catch (error) {
      console.error('[CrossTabManager] Error emitting event:', error);
    }
  }

  /**
   * Handle storage events from other tabs
   */
  handleStorageEvent(event) {
    // Ignore non-crossTab events
    if (!event.key || !event.key.startsWith('crossTab_')) {
      return;
    }

    // Ignore removals
    if (!event.newValue) {
      return;
    }

    try {
      const eventData = JSON.parse(event.newValue);
      const eventType = event.key.replace('crossTab_', '');

      // Check if event is stale
      if (this.isEventStale(eventData)) {
        // Stale event logging silenced
        return;
      }

      // Check for duplicate processing
      if (this.isDuplicateEvent(eventData)) {
        // Duplicate event logging silenced
        return;
      }

      // Mark as processed
      this.markEventProcessed(eventData);

      // Process the event
      this.processEvent(eventType, eventData);

    } catch (error) {
      console.error('[CrossTabManager] Error handling storage event:', error);
    }
  }

  /**
   * Process a cross-tab event
   */
  processEvent(eventType, eventData) {
    // Event processing logging silenced

    // Get handlers for this event type
    const handlers = this.eventHandlers.get(eventType) || [];
    const genericHandlers = this.eventHandlers.get('*') || [];

    // Execute specific handlers
    handlers.forEach(handler => {
      try {
        handler(eventData);
      } catch (error) {
        console.error(`[CrossTabManager] Handler error for ${eventType}:`, error);
      }
    });

    // Execute generic handlers
    genericHandlers.forEach(handler => {
      try {
        handler(eventType, eventData);
      } catch (error) {
        console.error('[CrossTabManager] Generic handler error:', error);
      }
    });
  }

  /**
   * Check if an event is stale
   */
  isEventStale(eventData) {
    if (!eventData.timestamp) {
      return false; // Can't determine, process it
    }

    const age = Date.now() - eventData.timestamp;
    return age > this.config.eventTTL;
  }

  /**
   * Check if we've already processed this event
   */
  isDuplicateEvent(eventData) {
    const eventKey = `${eventData.type}_${eventData.timestamp}_${eventData.tabId}`;
    
    if (this.processedEvents.has(eventKey)) {
      return true;
    }

    return false;
  }

  /**
   * Mark an event as processed
   */
  markEventProcessed(eventData) {
    const eventKey = `${eventData.type}_${eventData.timestamp}_${eventData.tabId}`;
    this.processedEvents.set(eventKey, Date.now());
  }

  /**
   * Clean up stale events from localStorage
   */
  cleanupStaleEvents() {
    try {
      const keysToRemove = [];

      // Find all crossTab events
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('crossTab_')) {
          try {
            const value = localStorage.getItem(key);
            if (value) {
              const event = JSON.parse(value);
              if (this.isEventStale(event)) {
                keysToRemove.push(key);
              }
            }
          } catch (e) {
            // If we can't parse it, remove it
            keysToRemove.push(key);
          }
        }
      }

      // Remove stale events
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        // Stale event removal logging silenced
      });

      // Also clean up old processed events from memory
      const cutoffTime = Date.now() - this.config.deduplicationWindow;
      for (const [key, timestamp] of this.processedEvents) {
        if (timestamp < cutoffTime) {
          this.processedEvents.delete(key);
        }
      }

    } catch (error) {
      console.error('[CrossTabManager] Error cleaning up events:', error);
    }
  }

  /**
   * Start periodic cleanup of stale events
   */
  startPeriodicCleanup() {
    this.eventCleanupInterval = setInterval(() => {
      this.cleanupStaleEvents();
    }, this.config.cleanupInterval);
  }

  /**
   * Register default event handlers
   */
  registerDefaultHandlers() {
    // Login event handler
    this.registerHandler('login', (eventData) => {
      // Cross-tab login logging silenced
      // Update local auth state
      if (eventData.data.authState) {
        cacheManager.saveAuthState(eventData.data.authState);
        
        // Reload the page to reinitialize with new auth
        window.location.reload();
      }
    });

    // Logout event handler
    this.registerHandler('logout', (eventData) => {
      // Cross-tab logout logging silenced
      // Perform logout in this tab
      logoutService.handleCrossTabLogout({
        type: 'logout_event',
        timestamp: eventData.timestamp,
        phase: 'initiated'
      });
    });

    // Data refresh event handler
    this.registerHandler('dataRefresh', (eventData) => {
      // Cross-tab data refresh logging silenced
      // Trigger data refresh in app store
      if (window.appStore && eventData.data.modules) {
        eventData.data.modules.forEach(module => {
          if (window.appStore[`${module}Handler`]) {
            window.appStore[`${module}Handler`].refresh();
          }
        });
      }
    });
  }

  /**
   * Get unique tab identifier
   */
  getTabId() {
    if (!this.tabId) {
      this.tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return this.tabId;
  }

  /**
   * Utility method to emit login event
   */
  emitLogin(authState) {
    this.emit('login', { authState });
  }

  /**
   * Utility method to emit logout event
   */
  emitLogout(reason = 'user_initiated') {
    this.emit('logout', { reason });
  }

  /**
   * Utility method to request data refresh in other tabs
   */
  emitDataRefresh(modules = []) {
    this.emit('dataRefresh', { modules });
  }
}

// Export singleton instance
export const crossTabManager = new CrossTabManager();

// Also export class for testing
export { CrossTabManager };